import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import reviewInstanceService from './reviewInstanceService';

import type { ReviewTypeValue } from '@/constants/review/CycleInstance';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { queryClient } from '@/AppWithProviders';
import { ReviewInstanceQueryKey, ReviewMutationKey, ReviewQueryKey } from '@/constants/QueryKeys';
import { getUtcOffsetMinutes } from '@/utils/dateUtils';

export { default as reviewInstanceService } from './reviewInstanceService';

const useSendReminder = () => {
    return useMutation<ReviewInstance, Error, ReviewInstance>({
        mutationKey: [ReviewMutationKey.SEND_REMINDER],
        mutationFn: (reviewInstance: ReviewInstance) =>
            reviewInstanceService.sendReminder(reviewInstance),
    });
};

const useGetReviewInstance = (reviewInstanceId: string) => {
    return useQuery<ReviewInstance, Error, ReviewInstance>({
        queryKey: [ReviewInstanceQueryKey.REVIEW_INSTANCE, reviewInstanceId],
        queryFn: () => reviewInstanceService.getReviewInstance(reviewInstanceId),
        enabled: !!reviewInstanceId,
    });
};

const useDeleteReviewInstance = () => {
    return useMutation({
        mutationFn: (reviewInstanceId: string) =>
            reviewInstanceService.deleteReviewInstance(reviewInstanceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED],
            });
        },
    });
};

const useGetLatestReviewInstanceIdByPeriod = (
    employeeId: string,
    reviewType: ReviewTypeValue,
    startDate: string,
    endDate: string,
) => {
    const utcOffsetMinutes = getUtcOffsetMinutes();

    return useQuery<string, Error>({
        queryKey: [
            ReviewInstanceQueryKey.REVIEW_INSTANCE,
            'latest-by-period',
            employeeId,
            reviewType,
            startDate,
            endDate,
            utcOffsetMinutes,
        ],
        queryFn: () =>
            reviewInstanceService.getLatestReviewInstanceIdByPeriod({
                employeeId: employeeId,
                reviewType: reviewType,
                startDate: startDate,
                endDate: endDate,
                utcOffsetMinutes,
            }),
        enabled: !!employeeId && !!reviewType && !!startDate && !!endDate,
    });
};

export {
    useSendReminder,
    useGetReviewInstance,
    useDeleteReviewInstance,
    useGetLatestReviewInstanceIdByPeriod,
};
